import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import {
    Box,
    Button,
    TextField,
    Typography,
    Paper,
    Container,
    CircularProgress,
    MenuItem,
    Select,
    InputLabel,
    FormControl
} from '@mui/material';
import { styled } from '@mui/material/styles';

// Configuración de axios
const api = axios.create({
    baseURL: 'https://dymesenergy.gst-panel.com'
});

// Servicios
const DepartamentoService = {
    obtenerTodos: () => api.get('/get/obtener_departamentos.php')
        .then(response => ({
            data: Array.isArray(response.data) ? response.data : response.data?.data || []
        }))
};

const MunicipioService = {
    obtenerPorDepartamento: (idDepartamento) =>
        api.get(`/get/obtener_municipios.php?id_departamento=${idDepartamento}`)
            .then(response => ({
                data: response.data.success ? response.data.data : [],
                success: response.data.success,
                message: response.data.message || ''
            }))
};

// Estilos
const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(4),
    marginTop: theme.spacing(4),
    borderRadius: '12px',
    boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
    marginBottom: theme.spacing(3),
    '& .MuiOutlinedInput-root': {
        borderRadius: '8px',
    },
}));

const StyledButton = styled(Button)(({ theme }) => ({
    padding: theme.spacing(1.5),
    borderRadius: '8px',
    fontWeight: 'bold',
    textTransform: 'none',
    fontSize: '16px',
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
    minWidth: '100%',
    marginBottom: theme.spacing(3),
    '& .MuiOutlinedInput-root': {
        borderRadius: '8px',
    },
}));

const RecursoSolarForm = () => {
    const [departamentoId, setDepartamentoId] = useState('');
    const [municipioId, setMunicipioId] = useState('');
    const [valor, setValor] = useState('');
    const [departamentos, setDepartamentos] = useState([]);
    const [municipios, setMunicipios] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingDepartamentos, setLoadingDepartamentos] = useState(true);
    const [loadingMunicipios, setLoadingMunicipios] = useState(false);
    const { enqueueSnackbar } = useSnackbar();

    // Cargar departamentos al iniciar
    useEffect(() => {
        const cargarDepartamentos = async () => {
            try {
                const response = await DepartamentoService.obtenerTodos();
                setDepartamentos(response.data);
            } catch (error) {
                enqueueSnackbar('Error al cargar departamentos', {
                    variant: 'error',
                    autoHideDuration: 5000
                });
                console.error('Error cargando departamentos:', error);
            } finally {
                setLoadingDepartamentos(false);
            }
        };

        cargarDepartamentos();
    }, [enqueueSnackbar]);

    // Cargar municipios cuando cambia el departamento
    useEffect(() => {
        if (!departamentoId) {
            setMunicipios([]);
            setMunicipioId('');
            return;
        }

        const cargarMunicipios = async () => {
            setLoadingMunicipios(true);
            try {
                const response = await MunicipioService.obtenerPorDepartamento(departamentoId);
                setMunicipios(response.data);
            } catch (error) {
                enqueueSnackbar('Error al cargar municipios', {
                    variant: 'error',
                    autoHideDuration: 5000
                });
                console.error('Error cargando municipios:', error);
            } finally {
                setLoadingMunicipios(false);
            }
        };

        cargarMunicipios();
    }, [departamentoId, enqueueSnackbar]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!departamentoId) {
            enqueueSnackbar('Debe seleccionar un departamento', {
                variant: 'error',
                autoHideDuration: 3000
            });
            return;
        }

        if (!municipioId) {
            enqueueSnackbar('Debe seleccionar un municipio', {
                variant: 'error',
                autoHideDuration: 3000
            });
            return;
        }

        if (!valor || isNaN(valor)) {
            enqueueSnackbar('El valor debe ser un número válido', {
                variant: 'error',
                autoHideDuration: 3000
            });
            return;
        }

        const valorNumerico = parseFloat(valor);
        if (valorNumerico <= 0 || valorNumerico > 10) {
            enqueueSnackbar('El valor debe estar entre 0.1 y 10', {
                variant: 'error',
                autoHideDuration: 3000
            });
            return;
        }

        setLoading(true);

        try {
            const response = await api.post('/agregar/guardar_recursosolar.php', {
                Id_ciudad: municipioId,
                Valor: valorNumerico
            }, {
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            });

            if (response.data.success) {
                enqueueSnackbar(`Recurso solar para el municipio agregado con éxito`, {
                    variant: 'success',
                    autoHideDuration: 5000,
                    anchorOrigin: {
                        vertical: 'top',
                        horizontal: 'right'
                    }
                });
                setDepartamentoId('');
                setMunicipioId('');
                setValor('');
            } else {
                throw new Error(response.data.message || 'Error al crear recurso solar');
            }
        } catch (error) {
            console.error('Error:', error);
            let errorMessage = 'Ocurrió un problema al agregar el recurso solar';

            if (error.response) {
                errorMessage = error.response.data.message || errorMessage;
            } else if (error.request) {
                errorMessage = 'No se pudo conectar con el servidor';
            }

            enqueueSnackbar(errorMessage, {
                variant: 'error',
                autoHideDuration: 5000,
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'right'
                }
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container maxWidth="md">
            <StyledPaper elevation={3}>
                <Typography variant="h4" component="h1" gutterBottom sx={{
                    fontWeight: 'bold',
                    color: 'primary.main',
                    marginBottom: 3
                }}>
                    Agregar Recurso Solar
                </Typography>

                <Box component="form" onSubmit={handleSubmit}>
                    <StyledFormControl variant="outlined" disabled={loadingDepartamentos || loading}>
                        <InputLabel id="departamento-label">Departamento</InputLabel>
                        <Select
                            labelId="departamento-label"
                            value={departamentoId}
                            onChange={(e) => {
                                setDepartamentoId(e.target.value);
                                setMunicipioId(''); // Reset municipio al cambiar departamento
                            }}
                            label="Departamento"
                            required
                        >
                            <MenuItem value="">
                                <em>Seleccione un departamento</em>
                            </MenuItem>
                            {departamentos.map((departamento) => (
                                <MenuItem key={departamento.Id} value={departamento.Id}>
                                    {departamento.Nombre}
                                </MenuItem>
                            ))}
                        </Select>
                    </StyledFormControl>

                    <StyledFormControl variant="outlined" disabled={!departamentoId || loadingMunicipios || loading}>
                        <InputLabel id="municipio-label">Municipio</InputLabel>
                        <Select
                            labelId="municipio-label"
                            value={municipioId}
                            onChange={(e) => setMunicipioId(e.target.value)}
                            label="Municipio"
                            required
                        >
                            <MenuItem value="">
                                <em>{municipios.length ? 'Seleccione un municipio' : 'Primero seleccione un departamento'}</em>
                            </MenuItem>
                            {municipios.map((municipio) => (
                                <MenuItem key={municipio.Id} value={municipio.Id}>
                                    {municipio.Nombre}
                                </MenuItem>
                            ))}
                        </Select>
                    </StyledFormControl>

                    <StyledTextField
                        fullWidth
                        label="Valor del Recurso Solar (0.1 - 10)"
                        variant="outlined"
                        type="number"
                        inputProps={{
                            step: "0.1",
                            min: "0.1",
                            max: "10"
                        }}
                        value={valor}
                        onChange={(e) => setValor(e.target.value)}
                        disabled={loading}
                        InputLabelProps={{
                            shrink: true,
                        }}
                        helperText="Ingrese el valor del recurso solar en kWh/m²/día"
                    />

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <StyledButton
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={loading || !municipioId || !valor}
                            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                        >
                            {loading ? 'Guardando...' : 'Guardar Recurso Solar'}
                        </StyledButton>
                    </Box>
                </Box>
            </StyledPaper>
        </Container>
    );
};

export default RecursoSolarForm;